import React, { Suspense, useRef } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { SnackbarProvider } from "notistack"
import { HTML5Backend } from "react-dnd-html5-backend"
import { DndProvider } from "react-dnd"

import { HashRouter, BrowserRouter } from "react-router-dom"

import services from "api/services"

import {
  CssBaseline,
  createMuiTheme,
  createTheme,
  makeStyles,
} from "@mui/material"
import { ThemeProvider, styled } from "@mui/material/styles"
import StylesProvider from "@mui/styles/StylesProvider"

import AppComponent from "pages/App/index"
import ErrorBoundaryFallback from "@osapiens/react-ui/lib/ui/ErrorBoundary"
import { osapiens } from "themes"

import { FALLBACK_LANGUAGE, setup } from "i18n"

import "./App.css"
import { isAte } from "utils/Global"

// const theme = osapiens.light
const theme = osapiens.light

const PREFIX = "App"

const classes = {
  success: `${PREFIX}-success`,
  error: `${PREFIX}-error`,
  warning: `${PREFIX}-warning`,
  info: `${PREFIX}-info`,
}

const StyledErrorBoundary = styled(ErrorBoundary)(({ theme }) => ({
  [`& .${classes.success}`]: { backgroundColor: theme.palette.success.main },
  [`& .${classes.error}`]: { backgroundColor: theme.palette.error.main },
  [`& .${classes.warning}`]: { backgroundColor: theme.palette.warning.main },
  [`& .${classes.info}`]: { backgroundColor: theme.palette.secondary.main },
}))

const CombinedStoreProvider: React.FC<{ provider: React.FC<any>[] }> = ({
  provider,
  children,
}) => {
  const InitialNode: React.FC<{}> = ({ children }) => <>{children}</>
  const NestedProvider = provider.reduce((Provider, ParentNode) => {
    return ({ children }) => (
      <ParentNode>
        <Provider>{children}</Provider>
      </ParentNode>
    )
  }, InitialNode)

  return <NestedProvider>{children}</NestedProvider>
}

// const updateFontFace = (fontFamily: string, fontUrl: string) => {
//   // Create a new style element
//   const styleElement = document.createElement('style');
//   styleElement.type = 'text/css';

//   // Construct the @font-face rule and universal font-family rule
//   const fontFaceRule = `
//     @font-face {
//       font-family: "${fontFamily}";
//       src: local("${fontFamily}"), url("${fontUrl}") format("opentype");
//     }

//     * {
//       font-family: "${fontFamily}", serif !important;
//     }
//   `;

//   styleElement.textContent = fontFaceRule;

//   // Remove any existing custom font-face styles to prevent duplicates
//   const existingFontStyle = document.getElementById('dynamic-font-face');
//   if (existingFontStyle) {
//     existingFontStyle.parentNode.removeChild(existingFontStyle);
//   }

//   // Append the new style element to the head
//   styleElement.id = 'dynamic-font-face';
//   document.head.appendChild(styleElement);
// }

const AppContainer = () => {
  React.useEffect(() => {
    // initialize i18n with fallback language if not done, yet
    setup(FALLBACK_LANGUAGE)
  }, [])

  const fontRef = useRef(null)

  return (
    <StyledErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <Suspense fallback={<div>loading...</div>}>
        <CombinedStoreProvider provider={services || []}>
          <SnackbarProvider
            maxSnack={3}
            classes={{
              variantSuccess: classes.success,
              variantError: classes.error,
              variantWarning: classes.warning,
              variantInfo: classes.info,
            }}
          >
            <DndProvider backend={HTML5Backend}>
              <StylesProvider injectFirst>
                <ThemeProvider theme={theme}>
                  {/* Kickstart a simple scoped CSS baseline to build upon. */}
                  {/* Required to override Material-UI's styles via CSS modules. */}
                  <CssBaseline />
                  <BrowserRouter>
                    <AppComponent />
                  </BrowserRouter>
                </ThemeProvider>
              </StylesProvider>
            </DndProvider>
          </SnackbarProvider>
        </CombinedStoreProvider>
      </Suspense>
    </StyledErrorBoundary>
  )
}

export default AppContainer
